import { FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, Form, Input, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import CopyableText from '../../components/copyable-text';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Payout } from '../../types/payout.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;

interface FilterValues {
	device_id: number | null;
	vendor_id: string | null;
	type: string | null;
	status: string | null;
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const Payouts = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [filters, setFilters] = useState<FilterValues>({
		device_id: null,
		vendor_id: null,
		type: null,
		status: null,
		date_range: null,
	});

	const { data, error, isLoading } = useQuery({
		queryKey: ['payouts', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				device_id: filters.device_id,
				vendor_id: filters.vendor_id,
				type: filters.type,
				status: filters.status,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/admin/payments/payouts', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const payouts: Payout[] = data?.results || [];

	const getStatusColor = (status: Payout['status']) => {
		switch (status) {
			case 'pending':
				return 'orange';
			case 'success':
				return 'green';
			case 'failed':
				return 'red';
			default:
				return 'default';
		}
	};

	const columns: ColumnsType<Payout> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: 'Device ID',
			dataIndex: 'device',
			key: 'device',
		},
		{
			title: 'Retailer',
			dataIndex: 'retailer',
			key: 'retailer',
			render: (retailer) => (
				<div>
					<span className="text-sm font-medium uppercase">{retailer.business_name}</span>
					<CopyableText
						text={retailer.vendor_id}
						className="text-sm text-gray-500"
					/>
				</div>
			),
		},
		{
			title: 'Amount',
			dataIndex: 'amount',
			key: 'amount',
			render: (amount: string) => `${formatNumber(amount)} UGX`,
		},
		{
			title: 'Type',
			dataIndex: 'type',
			key: 'type',
			render: (type: string) => (
				<Tag color={type === 'record_only' ? 'blue' : 'purple'}>{type === 'record_only' ? 'RECORD ONLY' : 'RECORD & TRX'}</Tag>
			),
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => <Tag color={getStatusColor(status as Payout['status'])}>{status.toUpperCase()}</Tag>,
		},
		{
			title: 'Created By',
			dataIndex: 'created_by',
			key: 'created_by',
			render: (created_by) => (
				<div className="flex items-center gap-2">
					{/* <Avatar
						src={created_by.dp}
						size="small"
					/> */}
					<span className="text-sm">
						{created_by.first_name} {created_by.last_name}
					</span>
				</div>
			),
		},
		{
			title: 'Transaction ID',
			dataIndex: 'transaction',
			key: 'transaction',
			render: (transaction) =>
				transaction ? (
					<CopyableText text={transaction.id.toString()}>
						<span className="text-sm text-blue-600">#{transaction.id}</span>
					</CopyableText>
				) : (
					<span className="text-sm text-gray-400">N/A</span>
				),
		},
		{
			title: 'Notes',
			dataIndex: 'notes',
			key: 'notes',
			render: (notes: string) => <div className="max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap">{notes || 'N/A'}</div>,
		},
		{
			title: 'Created At',
			dataIndex: 'created_at',
			key: 'created_at',
			render: (created_at: string) => dayjs(created_at).format('MMM DD, YYYY HH:mm'),
		},
	];

	const handleFilterApply = (values: FilterValues) => {
		setFilters(values);
		setPaginationConfig({
			...paginationConfig,
			page: 1, // Reset to first page when filtering
		});
	};

	const handleFilterReset = () => {
		form.resetFields();
		setFilters({
			device_id: null,
			vendor_id: null,
			type: null,
			status: null,
			date_range: null,
		});
		setPaginationConfig({
			...paginationConfig,
			page: 1,
		});
	};

	const statusOptions = [
		{ value: 'pending', label: 'Pending' },
		{ value: 'success', label: 'Success' },
		{ value: 'failed', label: 'Failed' },
	];

	return (
		<AppLayout
			title="Payouts"
			subtitle="Manage retailer payouts and transactions."
		>
			<Card className="mb-4 shadow-sm">
				<Form
					form={form}
					layout="horizontal"
					onFinish={handleFilterApply}
					initialValues={filters}
				>
					<div className="flex flex-wrap items-end gap-4">
						<Form.Item
							name="device_id"
							label="Device ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Device ID"
								type="number"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="vendor_id"
							label="Vendor ID"
							className="mb-0 min-w-[150px]"
						>
							<Input
								placeholder="Vendor ID"
								type="text"
								allowClear
							/>
						</Form.Item>

						<Form.Item
							name="type"
							label="Type"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select type"
								allowClear
								options={[
									{ value: 'record_only', label: 'Record Only' },
									{ value: 'record_and_trx', label: 'Record & Transaction' },
								]}
							/>
						</Form.Item>

						<Form.Item
							name="status"
							label="Status"
							className="mb-0 min-w-[150px]"
						>
							<Select
								placeholder="Select status"
								allowClear
								options={statusOptions}
							/>
						</Form.Item>

						<Form.Item
							name="date_range"
							label="Date Range"
							className="mb-0 min-w-[300px]"
						>
							<RangePicker
								allowClear
								format="YYYY-MM-DD"
								placeholder={['Start Date', 'End Date']}
							/>
						</Form.Item>

						<Form.Item className="mb-0 flex-shrink-0">
							<Space>
								<Button
									type="primary"
									htmlType="submit"
									icon={<FilterOutlined />}
								>
									Apply Filters
								</Button>
								<Button
									onClick={handleFilterReset}
									icon={<ReloadOutlined />}
								>
									Reset
								</Button>
							</Space>
						</Form.Item>
					</div>
				</Form>
			</Card>

			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={payouts}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
				/>
			</div>
		</AppLayout>
	);
};

export default Payouts;
